import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export const hasSupabaseConfiguration = Boolean(url && key);
export const supabase = hasSupabaseConfiguration ? createClient(url!, key!) : null;

export type PublicPlayer = {
  username: string;
  wins: number;
  draws: number;
  losses: number;
  card_count: number;
  founder_count: number;
};

export type PublicCard = {
  username: string;
  level: number;
  edition: "arcade" | "midnight" | "coastal" | "library";
  is_shiny: boolean;
  serial_number: number;
  is_founder: boolean;
  founder_username: string;
};

export type LeaderboardPlayer = PublicPlayer & {
  rank: number;
  battles: number;
  win_rate: number;
};

export type FounderRecord = {
  level: number;
  edition: PublicCard["edition"];
  founder_username: string;
  is_shiny: boolean;
  serial_number: number;
  forged_at: string;
};

export type PopulationRecord = {
  level: number;
  edition: PublicCard["edition"];
  total_cards: number;
  shiny_cards: number;
  highest_serial: number;
};

export type TerminalRank = {
  player_id: string;
  username: string;
  edition: PublicCard["edition"];
  score: string;
  target?: number;
  remaining?: string;
  required?: string;
  highest_card?: { level: number; is_founder: boolean };
};

export type TerminalBoard = {
  generated_at: string;
  scores: TerminalRank[];
  approaching: TerminalRank[];
};

const cache = new Map<string, { expires: number; data: unknown }>();

async function cachedRpc<T>(
  name: string,
  args: Record<string, unknown>,
  ttlMs = 15 * 60_000,
  fresh = false,
): Promise<T[]> {
  if (!supabase) return [];
  const key = `${name}:${JSON.stringify(args)}`;
  const existing = cache.get(key);
  if (!fresh && existing && existing.expires > Date.now()) return existing.data as T[];
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  const result = (data ?? []) as T[];
  cache.set(key, { data: result, expires: Date.now() + ttlMs });
  return result;
}

export const publicApi = {
  searchPlayers: (query: string, fresh = false) => cachedRpc<PublicPlayer>("web_search_players", { p_query: query, p_limit: 20 }, 5 * 60_000, fresh),
  playerCards: (username: string, fresh = false) => cachedRpc<PublicCard>("web_player_collection", { p_username: username }, 5 * 60_000, fresh),
  leaderboard: () => cachedRpc<LeaderboardPlayer>("web_battle_leaderboard", { p_limit: 50 }, 15 * 60_000),
  founders: () => cachedRpc<FounderRecord>("web_founders", {}, 60 * 60_000),
  population: () => cachedRpc<PopulationRecord>("web_card_population", {}, 60 * 60_000),
  terminalBoard: async (resetVersion: number) => {
    if (!supabase) throw new Error("The Terminal cannot reach the island server.");
    const { data, error } = await supabase.rpc("terminal_board", { p_reset_version: resetVersion });
    if (error) throw error;
    return data as TerminalBoard;
  },
};
